<template>
<div class="collapsible-box" :class="[type, isMobileOnly ? 'mobile-only' : 'all-screen-sizes', collapsed ? 'closed' : 'open']">
    <div v-if="!hideTitle" :tabindex="tabIndex ? tabIndex : 0" class="collapsible-head" @mousedown.prevent="collapser" @keydown.enter.prevent="collapser" @keydown.space.prevent="collapser">
        <div v-if="icon" class="icon-holder">
            <span class="icon" :class="icon"></span>
        </div>
        <div class="title">
            <slot name="title">{{ title }}</slot>
        </div>
        <div class="chevron" v-if="!hideChevron">
            <div class="arrow"></div>
        </div>
        <div class="sub-title">
            <slot name="subtitle"></slot>
        </div>
    </div>
    <div class="collapsible-content-wrapper" ref="collapsibleContentWrapper">
        <div class="collapsible-content">
            <slot></slot>
        </div>
    </div>
</div>
</template>

<script>
export default {
    //inject: ["$validator"],
    name: "collapsible-box",
    props: [
        "title",
        "type",
        "initCollapse",
        "icon",
        "hideChevron",
        "disableClick",
        "tabIndex",
        "hideTitle",
        "isMobileOnly"
    ],
    emits: ['close','open', 'collapsed'],
    data() {
        return {
            collapsed: Boolean(this.initCollapse),
            clearTabbing: null,
        };
    },
    watch: {
        initCollapse: function (val) {
            //this.initCollapse = val;
            this.collapsed = this.initCollapse;
        },
        collapsed(current, previous) {
            if (current) {
                this.clearTabbing = this.excludeTabbing(
                    this.$refs.collapsibleContentWrapper
                );
                this.$emit("close");
            } else {
                if (typeof this.clearTabbing === "function") {
                    this.clearTabbing();
                    this.clearTabbing = null;
                }
                this.$emit("open");
            }
        },
    },
    methods: {
        collapser() {
            if (!this.disableClick) {
                this.collapsed = !this.collapsed;
                this.$emit("collapsed", this.collapsed);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
$collapsible-box-transition-speed: 0.2s;

.collapsible-box {
    border-width: 1px;
    border-style: solid;
    padding: $spacer-three - 1 $spacer-four $spacer-four - 1;
    border-radius: 5px;
    margin-bottom: $spacer-three;

    .collapsible-head {
        @include unselectable;
        display: flex;
        transition: padding $collapsible-box-transition-speed;
        cursor: pointer;
        align-items: flex-start;

        // non-bootstrap columns
        .icon-holder {
            flex: 0 0 31px;
            height: 25px;

            .icon {
                position: relative;
                top: 3px;
            }
        }

        .title {
            flex: 1 0 0;
            font-weight: $font-weight-bold;
            width: 100%;
            padding-bottom: 20px !important;
        }

        .chevron {
            flex: 0 0 20px;
            text-align: center;
            margin-left: $spacer-two;
        }

        .sub-title {
            display: block;
            text-decoration: none;
            color: $grayscale-two;
            padding-top: 10px;
        }
    }

    .collapsible-content {
        padding-top: $spacer-three + 1;
        border-top-style: solid;
        border-top-width: 1px;
        width: 100%;

        > :last-child {
            padding-bottom: 0;
        }
    }

    /* ---------- color variations ---------- */

    @each $color,
    $value in $theme-colors {
        &.#{$color} {
            border-color: $value;

            .collapsible-head {
                color: $value;
            }

            .collapsible-content {
                border-top-color: $value;
            }

            .arrow::before,
            .arrow::after {
                background-color: $value;
            }

            .collapsible-head:focus {
                outline: none;

                .chevron {
                    box-shadow: 0 0 0 $spacer-two map-get($theme-thirties, $color);
                    border-radius: $spacer-two;
                }
            }
        }
    }

    &.plain {
        border-width: 0;
        padding: 0 0 $spacer-four;

        .collapsible-head {
            display: block;
            @include a-text;

            &:hover {
                //@include a-hover-text;
            }

            .title,
            .chevron {
                display: inline-block;
                margin-right: $spacer-three;
                font-weight: $font-weight-regular;
            }

            &:focus {
                outline: none;

                .chevron {
                    box-shadow: 0 0 0 $spacer-two $blue-thirty;
                    border-radius: $spacer-two;
                }
            }
        }

        .collapsible-content {
            border-top-width: 0;
            padding-top: $spacer-three;
        }

        .arrow::before,
        .arrow::after {
            background-color: $blue;
        }
    }

    /* ---------- chevron and chevron animation styles ---------- */

    .arrow {
        position: relative;
        width: 20px;
        height: 10px;
        display: inline-block;
        cursor: pointer;
        transition: transform $collapsible-box-transition-speed;

        &:before,
        &:after {
            height: 10px;
            width: 2px;
            content: "";
            position: absolute;
            border-radius: 1px;
            display: block;

            &:hover {
                background: red;
            }
        }

        &:before {
            left: 12px;
            transform: rotate(-45deg);
        }

        &:after {
            right: 12px;
            transform: rotate(45deg);
        }
    }

    .collapsible-content-wrapper {
        display: flex;
        padding: 0 $spacer-four;
        margin: 0 (-$spacer-four);

        &::after {
            content: "";
            height: 35px;
            max-height: 0px;
            transition: height $collapsible-box-transition-speed cubic-bezier(0.76, 0.37, 0.67, 0.9),
                max-height 0s $collapsible-box-transition-speed;
        }

        >div {
            transition: margin-bottom $collapsible-box-transition-speed cubic-bezier(0.24, 0.98, 0.26, 0.99),
                border-top-color $collapsible-box-transition-speed,
                opacity $collapsible-box-transition-speed $collapsible-box-transition-speed * 0.7;
            margin-bottom: 0;
            opacity: 1;
            max-height: 100000000px;
        }
    }

    @mixin closed {
        padding-bottom: $spacer-three - $spacer-two - 1;

        .collapsible-content {
            border-top-color: transparent;
        }

        .arrow {
            transform: rotate(180deg);
        }

        .collapsible-content-wrapper {
            &::after {
                height: 0;
                transition: height $collapsible-box-transition-speed cubic-bezier(0.67, 0.9, 0.76, 0.37),
                    opacity $collapsible-box-transition-speed $collapsible-box-transition-speed * 0.1;
                max-height: 35px;
            }

            >div {
                margin-bottom: -1000px;
                transition: margin-bottom $collapsible-box-transition-speed cubic-bezier(0.5, 0, 0.9, 0.8),
                    visibility 0s $collapsible-box-transition-speed,
                    max-height 0s $collapsible-box-transition-speed,
                    border-top-color $collapsible-box-transition-speed,
                    opacity $collapsible-box-transition-speed $collapsible-box-transition-speed * 0.1;
                visibility: hidden;
                opacity: 0;
                max-height: 0;
            }
        }
    }

    &.closed.all-screen-sizes {
        @include closed;
    }

    @mixin collapse-effect {
        .collapsible-content-wrapper {
            max-height: 0;
            transition: max-height 0.5s ease-in-out;
        }

        &.open {
            .collapsible-content-wrapper {
                max-height:1300px;;
            }
        }
    }

    &.all-screen-sizes {
        @include collapse-effect();
    }

    @include wide-mobile {
        &.closed.mobile-only {
            @include closed;
        }

        &.mobile-only {
            @include collapse-effect();
        }
    }
}
</style>
